import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Spinner, Alert, Card, Badge } from 'react-bootstrap';
import CryptoJS from 'crypto-js';
import api from '../api';

function AdminHouseGames() {
  const [dailyProfitLoss, setDailyProfitLoss] = useState(null);
  const [detailedGamesInfo, setDetailedGamesInfo] = useState(null);
  const [profitSummary, setProfitSummary] = useState(null);
  const [gameStats, setGameStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('daily');

  const getApiUrl = () => {
    const hashedApiVersion = localStorage.getItem('apiVersion');
    if (hashedApiVersion) {
      const version2Hash = CryptoJS.SHA256('2').toString();
      if (hashedApiVersion === version2Hash) {
        return 'https://phoenix-bingo-2.onrender.com/api';
      }
    }
    return process.env.REACT_APP_API;
  };

  const API = getApiUrl();

  const amharicDays = {
    'Monday': 'ሰኞ',
    'Tuesday': 'ማክሰኞ',
    'Wednesday': 'ረቡዕ',
    'Thursday': 'ሐሙስ',
    'Friday': 'ዓርብ',
    'Saturday': 'ቅዳሜ',
    'Sunday': 'እሑድ'
  };
  
  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const englishDate = new Date(dateString).toLocaleDateString(undefined, options);
    const [weekday, ...rest] = englishDate.split(',');
    return `${amharicDays[weekday]}${rest.join(',')}`;
  };
  
  const formatDateTime = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    const englishDateTime = new Date(dateString).toLocaleString(undefined, options);
    const [weekday, ...rest] = englishDateTime.split(',');
    return `${amharicDays[weekday]}${rest.join(',')}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const adminId = localStorage.getItem('adminId');

      if (!adminId) {
        setError('Admin ID not found. Please log in again.');
        setLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      try {
        // Fetch daily house profit/loss for the last 365 days
        const dailyProfitLossRes = await api.get(`${API}/admin/${adminId}/daily-house-profit-loss`);
        if (!dailyProfitLossRes) throw new Error('Failed to fetch daily profit/loss data');
        const dailyProfitLossData = dailyProfitLossRes;
        setDailyProfitLoss(dailyProfitLossData);

        // Fetch detailed house games info
        const detailedGamesInfoRes = await api.get(`${API}/admin/${adminId}/detailed-house-games-info`);
        console.log(detailedGamesInfoRes);
        if (!detailedGamesInfoRes) throw new Error('Failed to fetch detailed games info');
        const detailedGamesInfoData = detailedGamesInfoRes;
        setDetailedGamesInfo(detailedGamesInfoData);

        // Fetch house profit summary
        const profitSummaryRes = await api.get(`${API}/admin/${adminId}/house-profit-summary`);
        if (!profitSummaryRes) throw new Error('Failed to fetch profit summary');
        const profitSummaryData = profitSummaryRes;
        setProfitSummary(profitSummaryData);

        // Fetch house game stats
        const gameStatsRes = await api.get(`${API}/admin/house-game-stats/${adminId}`);
        if (!gameStatsRes) throw new Error('Failed to fetch game stats');
        const gameStatsData = gameStatsRes;
        setGameStats(gameStatsData);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('An error occurred while fetching data. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, [API]);

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" role="status" variant="primary">
          <span className="sr-only">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <div className='w-full'>
        <h3 className="text-teal-200 text-center">Online Games Dashboard</h3>
        <div className="flex justify-center gap-4 mb-4">
        <button
          className={`px-6 py-2 rounded-t-lg ${
            activeTab === 'daily'
              ? 'bg-teal-800 text-white'
              : 'bg-teal-200 text-black-900 hover:bg-teal-800'
          }`}
          onClick={() => setActiveTab('daily')}
        >
          Daily Summary
        </button>
        <button
          className={`px-6 py-2 rounded-t-lg ${
            activeTab === 'detailed'
              ? 'bg-teal-800 text-white'
              : 'bg-teal-200 text-black-900 hover:bg-teal-800'
          }`}
          onClick={() => setActiveTab('detailed')}
        >
          Detailed Games
        </button>
      </div>
        {activeTab === 'daily' && dailyProfitLoss && (
          <div className="w-full bg-teal-700 p-2 m-0">
            <div>
                <table className='w-full bg-teal-700 text-white'>
                  <thead>
                    <tr className='bg-teal-800 text-white p-2'>
                      <th className='p-2'>ቀን</th>
                      <th className='p-2'>የዕለት ጨዋታዎች</th>
                      <th className='p-2'>ትርፍ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyProfitLoss.dailyProfits.slice().reverse().map((day, index) => (
                      <tr key={day.date} className={`${ index % 2 === 0 ? 'bg-teal-700 text-white' : 'bg-teal-600 text-white'}`}>
                        <td className='py-2.5 px-3'>
                          {formatDate(day.date)}
                        </td>
                        <td className='py-2.5 px-3'>
                          {day.totalGames}
                        </td>
                        <td className='py-2.5 px-3'>
                          {day.totalProfit.toFixed(2)} Birr
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
          </div>
        )}

        {activeTab === 'detailed' && detailedGamesInfo && (
          <div className="w-full bg-teal-700 p-2 m-0">
            <div>
              <div>
                <table className='w-full bg-teal-700 text-white' >
                  <thead>
                    <tr className='bg-teal-900 text-white'>
                      <th className='p-2'>ቀን</th>
                      <th className='p-2'>መጠን</th>
                      <th className='p-2'>ተጫዋቾች</th>
                      <th className='p-2'>የኦንላይን  ተጫዋቾች</th>
                      <th className='p-2'>ሁኔታ</th>
                      <th className='p-2'>ትርፍ</th>
                    </tr>
                  </thead>
                  <tbody>
                  {detailedGamesInfo.days.map((day) => (
                    day.games.map((game, index) => (
                      <tr key={game.gameId} className={`${index % 2 == 0 ? 'bg-teal-700 text-white' : 'bg-teal-600 text-white'}`}>
                        <td>
                          {formatDateTime(game.createdAt)}
                        </td>
                        <td className='py-2.5 px-3'>
                          {game.bettingAmount} Birr
                        </td>
                        <td className='py-2.5 px-3'>
                          {game.numberOfPlayers}
                        </td>
                        <td className='py-2.5 px-3'>
                          {game.houseCardCount}
                        </td>
                        <td className='py-2.5 px-3'>
                            {game.status}
                        </td>
                        <td className='py-2.5 px-3'>
                          {game.profit.toFixed(2)} Birr
                        </td>
                      </tr>
                    ))
                  ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}

export default AdminHouseGames;